import { useState } from 'react';
import { Check, ChevronsUpDown, MapPin } from 'lucide-react';
import type {
  CustomerAccountRole,
  CustomerAddress,
  CustomerOption,
  ExceptionField,
} from '@/api/cockpit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  buildCatalogCustomerOptions,
  resolveMatchedCustomerDisplay,
  resolveMatchedFieldValue,
} from '../helpers';

export interface CustomerAccountPickerProps {
  role: CustomerAccountRole;
  field: ExceptionField;
  fields: Record<string, string>;
  onFieldChange: (key: string, value: string) => void;
  disabled: boolean;
  searchValue: string;
  debouncedSearchValue: string;
  onSearchChange: (value: string) => void;
  catalogOptions: readonly CustomerOption[];
  relatedBillToSelection?: CustomerOption | null;
  onCustomerAccountSelect?: (role: CustomerAccountRole, option: CustomerOption) => void;
}

interface CustomerCandidateView {
  id: string;
  customerNumber: string;
  name: string;
  confidence?: number;
  address: CustomerAddress | undefined;
  invoiceAccount?: string | null;
  invoiceAddress?: CustomerAddress;
  isStoppedAll: boolean | undefined;
}

export function CustomerAccountPicker(props: CustomerAccountPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<CustomerCandidateView | null>(null);
  const resolvedDisplay = resolveMatchedCustomerDisplay(
    props.field,
    props.catalogOptions,
    props.fields,
  );
  const selectedValue = resolveMatchedFieldValue(props.field, props.fields);
  const selectedCandidate = props.field.matchedCandidates?.find(
    (row) => row.value === selectedValue,
  );
  const selectedOption = props.catalogOptions.find((row) => row.customerNumber === selectedValue);
  const relatedBillTo =
    props.role === 'bill_to' &&
    props.relatedBillToSelection?.customerNumber === selectedValue
      ? props.relatedBillToSelection
      : null;
  const retainedSelection =
    selectedSnapshot?.customerNumber === selectedValue ? selectedSnapshot : null;
  const display = retainedSelection
    ? `${retainedSelection.customerNumber} - ${retainedSelection.name}`
    : relatedBillTo?.display ?? resolvedDisplay;
  const selectedAddress =
    selectedOption?.address ??
    relatedBillTo?.address ??
    retainedSelection?.address ??
    selectedCandidate?.address;
  const selectedStopped =
    selectedOption?.isStoppedAll === true ||
    retainedSelection?.isStoppedAll === true ||
    selectedCandidate?.isStoppedAll === true;
  const roleLabel = props.role === 'ship_to' ? 'Ship-to' : 'Bill-to';

  const select = (candidate: CustomerCandidateView) => {
    if (props.disabled) return;
    setSelectedSnapshot(candidate);
    props.onFieldChange(props.field.key, candidate.customerNumber);
    if (props.role === 'ship_to') {
      props.onFieldChange('invoice_customer_number', candidate.invoiceAccount?.trim() || candidate.customerNumber);
    }
    props.onCustomerAccountSelect?.(props.role, {
      id: candidate.id,
      customerNumber: candidate.customerNumber,
      name: candidate.name,
      display: `${candidate.customerNumber} - ${candidate.name}`,
      role: props.role,
      address: candidate.address ?? emptyAddress(),
      invoiceAccount: candidate.invoiceAccount ?? null,
      ...(candidate.invoiceAddress ? { invoiceAddress: candidate.invoiceAddress } : {}),
      ...(candidate.isStoppedAll !== undefined ? { isStoppedAll: candidate.isStoppedAll } : {}),
    });
    setOpen(false);
  };

  return (
    <div className="space-y-2">
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          if (props.disabled) return;
          setOpen(nextOpen);
          props.onSearchChange('');
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={`Select ${roleLabel.toLowerCase()} account`}
            className="h-auto min-h-10 w-full justify-between bg-background py-2 font-normal"
            title={display}
            disabled={props.disabled}
          >
            <span className="flex min-w-0 items-center gap-2 text-left">
              <Badge variant="outline" className="shrink-0 text-[10px] uppercase tracking-wide">
                {roleLabel}
              </Badge>
              <span className="truncate">
                {display || `Select ${roleLabel.toLowerCase()} account`}
              </span>
              {selectedStopped ? <StoppedBadge /> : null}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[min(38rem,calc(100vw-2rem))] border bg-background p-0 text-foreground shadow-lg"
          align="start"
        >
          <Command className="bg-background text-foreground" shouldFilter={false}>
            <CommandInput
              placeholder={`Search ${roleLabel.toLowerCase()} by account, name, or address...`}
              value={props.searchValue}
              onValueChange={props.onSearchChange}
            />
            <CommandList className="max-h-[28rem]">
              {props.debouncedSearchValue.length >= 2 ? (
                <CommandEmpty>No {roleLabel.toLowerCase()} account found.</CommandEmpty>
              ) : null}
              {(props.field.matchedCandidates ?? []).length > 0 ? (
                <CommandGroup heading={`Suggested ${roleLabel.toLowerCase()} candidates`}>
                  {(props.field.matchedCandidates ?? []).map((candidate) => (
                    <CustomerCandidateItem
                      key={`customer-candidate-${candidate.id}`}
                      roleLabel={roleLabel}
                      selected={selectedValue === candidate.value}
                      candidate={{
                        id: candidate.id,
                        customerNumber: candidate.value,
                        name: candidate.label.replace(`${candidate.value} - `, ''),
                        confidence: candidate.confidence,
                        address: candidate.address,
                        invoiceAccount: candidate.invoiceAccount ?? null,
                        ...(candidate.invoiceAddress ? { invoiceAddress: candidate.invoiceAddress } : {}),
                        isStoppedAll: candidate.isStoppedAll,
                      }}
                      onSelect={select}
                    />
                  ))}
                </CommandGroup>
              ) : null}
              <CommandSeparator />
              <CommandGroup heading={`${roleLabel} account search`}>
                {props.debouncedSearchValue.length < 2 ? (
                  <div className="px-3 py-4 text-sm text-muted-foreground">
                    Type at least 2 characters from the account, customer name, city, state or
                    province, postal code, country, or street.
                  </div>
                ) : (
                  buildCatalogCustomerOptions(
                    props.catalogOptions,
                    props.field.matchedCandidates ?? [],
                  ).map((option) => (
                    <CustomerCandidateItem
                      key={`customer-option-${option.id}`}
                      roleLabel={roleLabel}
                      selected={selectedValue === option.customerNumber}
                      candidate={{
                        id: option.id,
                        customerNumber: option.customerNumber,
                        name: option.name,
                        address: option.address,
                        invoiceAccount: option.invoiceAccount ?? null,
                        ...(option.invoiceAddress ? { invoiceAddress: option.invoiceAddress } : {}),
                        isStoppedAll: option.isStoppedAll,
                      }}
                      onSelect={select}
                    />
                  ))
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedValue ? (
        <SelectedAddressSummary
          roleLabel={roleLabel}
          display={display || selectedValue}
          address={selectedAddress}
          isStoppedAll={selectedStopped}
        />
      ) : null}
    </div>
  );
}

function CustomerCandidateItem(props: {
  roleLabel: string;
  selected: boolean;
  candidate: CustomerCandidateView;
  onSelect: (candidate: CustomerCandidateView) => void;
}): JSX.Element {
  return (
    <CommandItem
      value={`${props.candidate.customerNumber} ${props.candidate.name} ${addressSearchValue(props.candidate.address)}`}
      className="items-start gap-2 border-b border-border/60 px-3 py-3 last:border-b-0"
      onSelect={() => props.onSelect(props.candidate)}
    >
      <Check
        className={cn('mt-1 h-4 w-4 shrink-0', props.selected ? 'opacity-100' : 'opacity-0')}
      />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
            {props.roleLabel}
          </Badge>
          <span className="font-mono text-xs font-semibold">{props.candidate.customerNumber}</span>
          <span className="min-w-0 text-sm font-medium">{props.candidate.name}</span>
          {props.candidate.isStoppedAll === true ? <StoppedBadge /> : null}
        </div>
        <AddressLines address={props.candidate.address} />
      </div>
      {props.candidate.confidence !== undefined ? (
        <span className="shrink-0 pt-1 text-xs tabular-nums text-muted-foreground">
          {Math.round(props.candidate.confidence * 100)}%
        </span>
      ) : null}
    </CommandItem>
  );
}

function SelectedAddressSummary(props: {
  roleLabel: string;
  display: string;
  address: CustomerAddress | undefined;
  isStoppedAll: boolean;
}): JSX.Element {
  const addressLines = formatCompactAddressLines(props.address);
  return (
    <div className="rounded-md border border-border/80 bg-muted/30 px-2.5 py-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Selected {props.roleLabel}
        </span>
        {props.isStoppedAll ? <StoppedBadge /> : null}
        <span className="min-w-0 break-words text-xs font-semibold text-foreground">
          {props.display}
        </span>
      </div>
      {addressLines.length > 0 ? (
        <div className="mt-1 break-words text-xs leading-4 text-muted-foreground">
          {addressLines.map((line, index) => (
            <div key={`${index}-${line}`}>{line}</div>
          ))}
        </div>
      ) : (
        <div className="mt-1 text-xs italic text-muted-foreground">
          Address details unavailable
        </div>
      )}
    </div>
  );
}

function AddressLines(props: { address: CustomerAddress | undefined }): JSX.Element {
  const lines = formatAddressLines(props.address);
  if (lines.length === 0) {
    return <div className="text-xs italic text-muted-foreground">Address details unavailable</div>;
  }
  return (
    <div className="break-words text-xs text-muted-foreground">
      {lines.map((line, index) => (
        <div key={`${index}-${line}`}>{line}</div>
      ))}
    </div>
  );
}

export function formatAddressLines(address?: CustomerAddress): string[] {
  if (!address) return [];
  const locality = [address.city, address.stateOrProvince, address.postalCode]
    .filter((value): value is string => Boolean(value?.trim()))
    .join(', ');
  return [address.description, address.street, locality, address.country]
    .filter((value): value is string => Boolean(value?.trim()))
    .map((value) => value.trim());
}

export function formatCompactAddressLines(address?: CustomerAddress): string[] {
  if (!address) return [];
  const locality = [address.city, address.stateOrProvince, address.postalCode]
    .filter((value): value is string => Boolean(value?.trim()))
    .join(', ');
  return [
    [address.description, address.street]
      .filter((value): value is string => Boolean(value?.trim()))
      .map((value) => value.trim())
      .join(' · '),
    [locality, address.country]
      .filter((value): value is string => Boolean(value?.trim()))
      .map((value) => value.trim())
      .join(' · '),
  ].filter(Boolean);
}

function emptyAddress(): CustomerAddress {
  return {
    description: null,
    street: null,
    city: null,
    stateOrProvince: null,
    postalCode: null,
    country: null,
  };
}

function addressSearchValue(address?: CustomerAddress): string {
  return formatAddressLines(address).join(' ');
}

function StoppedBadge(): JSX.Element {
  return (
    <Badge variant="destructive" className="shrink-0 uppercase tracking-wide">
      Stopped
    </Badge>
  );
}
